// The Excel-like grid (spec §3.5–3.6): TanStack Table for the column model, sorting and
// selection, TanStack Virtual for the rows. Sticky header, sticky left columns (select, expand,
// Review, Routing window, CUSTOMER) and the sticky Ship column on the right.
//
// Keys (`cell-keys.ts`, as the Shipping schedule): arrows and Tab / Shift+Tab move, Home / End to the
// row's first / last cell; Enter, F2, a second click or typing edits; Esc cancels;
// Delete or Backspace clears; Space opens the pick tickets; Ctrl+Z undoes the last save. A locked (grey) or
// calculated (blue) cell answers typing with the big red message. Fill down as Excel (`fill.ts`): Shift+click
// or Shift+↑ ↓ takes a range down an editable column, Ctrl+D fills it from its top cell (no range: from the
// cell above), the fill handle in the cell's corner fills the rows it is dragged over, a pasted column fills
// one row each; one Ctrl+Z undoes the whole fill. The rows keep the order the person saw
// under the same view (`orderKey`, `stable-order.ts`); a saved line's row is marked 1.5 s.

import { useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ClipboardEvent, KeyboardEvent, ReactNode, RefObject } from "react";
import { getCoreRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import type { ColumnDef as TsColumnDef, RowSelectionState, SortingState } from "@tanstack/react-table";
import { defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronRightIcon,
  CornerDownRightIcon,
  TriangleAlertIcon,
  PanelRightOpenIcon,
  TruckIcon,
} from "lucide-react";
import {
  EXCEL_SOURCE_LABEL,
  ROUTING_LINES,
  a2000Lines,
  a2000Text,
  deposcoShippedLines,
  deposcoShippedText,
  deposcoStatusText,
  goneWhy,
  cleanListValue,
  countPrefix,
  customerFaceOf,
  dayOfWeek,
  formatDay,
  carriedFromLabel,
  isDay,
  isMulti,
  joinValues,
  multiValueProblem,
  splitValues,
  appointmentCell,
  chipsOfFilter,
  compareSortValues,
  divisionChips,
  divisionsNote,
  inChips,
  mixedText,
  poCount,
  poPtText,
  poText,
  ptCount,
  ptDivision,
  ptText,
  outsideText,
  readyAfterStart,
  isTeamReviewField,
  lineOrderKey,
  ptChangeText,
  PE_CLEARED,
  estimateText,
} from "@app/shared";
import type {
  A2000Shipped,
  ChangedField,
  DeposcoShipped,
  DeposcoStatus,
  LineSource,
  LineView,
  ListValueRecord,
  PtChange,
  PtView,
  Mode,
  Row,
  WarehouseField,
} from "@app/shared";
import {
  Button,
  Checkbox,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  toast,
  useIsMobile,
} from "@axny/platform-ui";
import { cn } from "@/lib/utils";
import type { ColumnDef } from "./columns";
import {
  DETAILS_WIDTH,
  EXPAND_WIDTH,
  SELECT_WIDTH,
  SHIP_WIDTH,
  enterOpensDetails,
  gridGeometry,
  rightWidth,
} from "./columns";
import {
  DateEditor,
  ListEditor,
  MomentEditor,
  MultiListEditor,
  SelectEditor,
  SuggestEditor,
  TextEditor,
} from "./editors";
import type { EditorProps, Move } from "./editors";
import { useViewOnlyNotice } from "./can-write";
import { useMessage } from "./message";
import { changedAt, useChanges } from "./line-history";
import { cellAfter, cellKey, rangeKey } from "./cell-keys";
import { canEditCell, cellValue, checkCellValue, fillRows, pastedValues, planFill } from "./fill";
import type { FillTarget } from "./fill";
import { runFill } from "./fill-run";
import { refocusEditor } from "./refocus";
import { HeaderMenu } from "./header-menu";
import { DivisionText, NamedText, customerItems } from "./named";
import type { Writer } from "./writer";
import { FinishedChip, PreRoutedMarker, ReviewChip, SavingMark, WindowChip, lineStateClass } from "./chips";
import { SourceBadge } from "./source-filter";
import type { SeenOrder } from "./stable-order";
import { shownOrder } from "./stable-order";

const ROW_HEIGHT = 37;
const HEADER_HEIGHT = 40;

export interface GridProps {
  mode: Mode;
  rows: Row[];
  columns: ColumnDef[];
  today: string;
  manager: boolean;
  /**
   * Whether the person may change anything (`routingWrite`): false for a Viewer — no editor, fill,
   * paste, Clear, Undo, approve, "2 values" pick or Ship; the cursor, copy, ticks and Details stay.
   */
  canWrite: boolean;
  rowColors: boolean;
  writer: Writer;
  /** Every line on the page, finished ones too: Undo and the list counts need them. */
  allLines: readonly LineView[];
  listValues: readonly ListValueRecord[];
  usage: (column: ColumnDef) => ReadonlyMap<string, number>;
  selection: RowSelectionState;
  onSelectionChange: (s: RowSelectionState) => void;
  sorting: SortingState;
  onSortingChange: (s: SortingState) => void;
  columnFilters: Record<string, string[]>;
  onColumnFilter: (column: string, values: string[]) => void;
  onAddValue: (column: ColumnDef, typed: string, done: (value: string | null) => void) => void;
  onManageList: (column: ColumnDef) => void;
  /**
   * The lines a column's "Show values" lists from: those every OTHER filter leaves, so a second value
   * can be ticked with the column's own filter on. Absent: the grid's rows.
   */
  headerRows?: (columnId: string) => readonly Row[];
  /**
   * An Appointment date entered (YYYY-MM-DD, "" cleared) on these lines — the row's, or every
   * selected line's when the row is one of several selected: the page schedules them (item 8).
   */
  onAppointment?: (lines: LineView[], date: string, done: () => void) => void;
  /** A row's Details (the pinned right part): the line's details panel. */
  onDetails?: (line: LineView) => void;
  /** The Ship column's Mark shipped (Worklist). */
  onShip?: (line: LineView) => void;
  /** A line to show once it is on the grid (Go to line): scrolled to, its pick tickets open. */
  focus?: string;
  /**
   * The view the rows are shown in: its filters, tab, Source, warehouses and sort, one string. While
   * it is the same, every row keeps the place the person saw it in (`stable-order.ts`): a save that
   * changes a line's window state never moves it; rows new to the view come after.
   */
  orderKey: string;
  height: string;
  /** The grid's own element, for a dialog to give focus back to (so Ctrl+Z reaches it). */
  gridRef?: RefObject<HTMLDivElement | null>;
}

interface Cursor {
  row: number;
  col: number;
}

/**
 * The cell being edited, by line key — not by row position: a refetch can reorder the rows while
 * the editor is open, and the value must still go to the line it was typed for.
 */
interface Editing {
  key: string;
  col: number;
  initial?: string;
}

const kindClass: Record<ColumnDef["kind"], string> = {
  system: "cell-locked",
  calc: "cell-calc",
  edit: "cell-edit",
};

const headerBar: Record<ColumnDef["kind"], string> = {
  system: "before:bg-slate-400",
  calc: "before:bg-blue-400",
  edit: "before:bg-emerald-400",
};

const headerHint: Record<ColumnDef["kind"], string> = {
  system: "From the systems — locked",
  calc: "Calculated by the tool",
  edit: "Warehouse fills in",
};

/** The workbook's row colours, from Production Notes and the routing status. */
function rowTone(line: LineView): string {
  const p = line.fields.productionNotes.toUpperCase();
  // A finished line is red only when it was cancelled; a shipped one has all its pick tickets gone too.
  const cancelled = line.finished ? line.finished.status === "Cancelled" : line.allGone;
  if (cancelled || line.fields.routingStatus === "Cancelled" || p.startsWith("CANCEL")) return "row-cancel";
  if (p.startsWith("BACK ORDER")) return "row-back";
  if (p.startsWith("READY")) return "row-ready";
  return "";
}

export function RoutingGrid(props: GridProps) {
  const { mode, rows, today, writer, manager, canWrite } = props;
  // On a phone the pinned columns would fill the screen: only select and expand stay pinned.
  const phone = useIsMobile();
  const columns = useMemo(
    () => (phone ? props.columns.map((c) => ({ ...c, sticky: false })) : props.columns),
    [phone, props.columns],
  );
  // The pinned right part — Details, and Ship on the Worklist — scrolls on a phone.
  const shipPin: CSSProperties = phone ? {} : { position: "sticky", right: 0 };
  const message = useMessage();
  const scrollRef = useRef<HTMLDivElement>(null);
  const cellRef = useRef<HTMLDivElement | null>(null);
  const [cursor, setCursor] = useState<Cursor>({ row: 0, col: 0 });
  const [editing, setEditing] = useState<Editing | null>(null);
  /** The "2 values" chooser that is open: its line and field (one at a time). */
  const [choosing, setChoosing] = useState<{ key: string; field: WarehouseField } | null>(null);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleExpanded = (key: string) => setExpanded((x) => ({ ...x, [key]: !x[key] }));
  // History: finished lines, no ticks either. `locked`: nothing on the grid writes — History, or a Viewer.
  const readOnly = mode === "history";
  const locked = readOnly || !canWrite;
  // The pinned right part's Ship: the Worklist's, for someone who may write.
  const ships = mode === "worklist" && canWrite;
  const viewOnly = useViewOnlyNotice();

  const tsColumns = useMemo<TsColumnDef<Row>[]>(
    () =>
      columns.map((c) => ({
        id: c.id,
        accessorFn: (r: Row) => (c.sortValue ? c.sortValue(r) : c.value(r)),
        // One order, reversed for Z → A (`compareSortValues`): TanStack's "alphanumeric" put "10SPOT" first both ways.
        sortingFn: (x, y, id) => compareSortValues(x.getValue(id), y.getValue(id)),
      })),
    [columns],
  );

  const table = useReactTable({
    data: rows,
    columns: tsColumns,
    getRowId: (r) => r.line.key,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    state: { sorting: props.sorting, rowSelection: props.selection },
    onSortingChange: (u) => props.onSortingChange(typeof u === "function" ? u(props.sorting) : u),
    onRowSelectionChange: (u) => props.onSelectionChange(typeof u === "function" ? u(props.selection) : u),
    enableRowSelection: !readOnly,
  });
  // The order the person saw, kept under the same view (`orderKey`): a line a save moves stays put
  // until they change a filter, the sort or the tab, press Clear filters, or reload.
  const tableRows = table.getRowModel().rows;
  const seen = useRef<SeenOrder<string> | null>(null);
  const sorted = useMemo(
    () => shownOrder(tableRows, (r) => lineOrderKey(r.original.line), props.orderKey, seen.current),
    [tableRows, props.orderKey],
  );
  useEffect(() => {
    seen.current = { filters: props.orderKey, order: sorted.map((r) => lineOrderKey(r.original.line)) };
  }, [sorted, props.orderKey]);

  // One virtual item per line, plus one for its pick tickets when open.
  const items = useMemo(() => {
    const out: { kind: "line" | "pts"; index: number }[] = [];
    sorted.forEach((r, index) => {
      out.push({ kind: "line", index });
      if (expanded[r.id]) out.push({ kind: "pts", index });
    });
    return out;
  }, [sorted, expanded]);
  const itemOfRow = useMemo(() => {
    const m = new Map<number, number>();
    items.forEach((it, i) => it.kind === "line" && m.set(it.index, i));
    return m;
  }, [items]);
  const rowOfKey = useMemo(() => new Map(sorted.map((r, i) => [r.id, i])), [sorted]);
  const editRow = editing ? rowOfKey.get(editing.key) : undefined;
  const editItem = editRow === undefined ? undefined : itemOfRow.get(editRow);
  // A fill down's cells, by line key (`from` where it started, `to` where it reaches): one column.
  const [range, setRange] = useState<{ col: number; from: string; to: string } | null>(null);
  const rangeRows = range ? { from: rowOfKey.get(range.from), to: rowOfKey.get(range.to) } : null;
  const rangeLo =
    rangeRows?.from === undefined || rangeRows.to === undefined ? -1 : Math.min(rangeRows.from, rangeRows.to);
  const rangeHi = rangeLo < 0 ? -1 : Math.max(rangeRows!.from!, rangeRows!.to!);
  // The fill handle being dragged: its row stays rendered (its pointer capture) while it scrolls away.
  const dragging = useRef<{ col: number; from: string } | null>(null);
  const dragItem = range && dragging.current ? itemOfRow.get(rowOfKey.get(dragging.current.from) ?? -1) : undefined;

  // The edited line left the page (merged, finished, filtered away by a refetch): the edit ends.
  useEffect(() => {
    if (editing && editRow === undefined) setEditing(null);
  }, [editing, editRow]);

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => {
      const it = items[i]!;
      return it.kind === "line" ? ROW_HEIGHT : 30 * (sorted[it.index]!.original.line.pts.length + 2) + 12;
    },
    // Measured sizes are cached by key, not position: by position, the row that moves up into a
    // closed pick-ticket panel's place would keep the panel's height.
    getItemKey: (i) => {
      const it = items[i]!;
      const key = sorted[it.index]!.id;
      return it.kind === "line" ? key : `${key}:pts`;
    },
    overscan: 12,
    // The row being edited stays rendered while it is scrolled out of view, so its editor (and
    // what was typed) survives the scroll, as in Excel.
    rangeExtractor: (shownRange) => {
      const shown = defaultRangeExtractor(shownRange);
      const keep = [editItem, dragItem].filter((i): i is number => i !== undefined && !shown.includes(i));
      return keep.length ? [...shown, ...keep].sort((a, b) => a - b) : shown;
    },
  });

  // Go to line: once its row is there, the cursor on it, scrolled into view, its pick tickets open.
  const focused = useRef(false);
  useEffect(() => {
    const row = props.focus ? rowOfKey.get(props.focus) : undefined;
    if (focused.current || row === undefined) return;
    focused.current = true;
    setExpanded((x) => ({ ...x, [props.focus!]: true }));
    setCursor({ row, col: 0 });
    const item = itemOfRow.get(row);
    if (item === undefined) return;
    requestAnimationFrame(() => {
      virtualizer.scrollToIndex(item, { align: "center" });
      // The page too, when the grid starts below the fold (a short window).
      scrollRef.current?.scrollIntoView({ block: "nearest" });
    });
  }, [props.focus, rowOfKey, itemOfRow, virtualizer]);

  // Column geometry: the pinned offsets and every column's left edge.
  const geometry = useMemo(() => gridGeometry(columns, mode, phone, canWrite), [columns, mode, phone, canWrite]);

  const rowCount = sorted.length;
  const at = (r: number) => sorted[r]?.original;
  const col = (c: number) => columns[c];

  // Keep the cursor on the grid when rows or columns change.
  useEffect(() => {
    setCursor((cur) => ({
      row: Math.min(cur.row, Math.max(0, rowCount - 1)),
      col: Math.min(cur.col, columns.length - 1),
    }));
  }, [rowCount, columns.length]);

  const scrollTo = (c: Cursor) => {
    const item = itemOfRow.get(c.row);
    if (item !== undefined) virtualizer.scrollToIndex(item, { align: "auto" });
    const el = scrollRef.current;
    const def = col(c.col);
    if (!el || !def || def.sticky) return;
    const left = geometry.lefts[c.col]!;
    if (left - el.scrollLeft < geometry.pinned) el.scrollLeft = left - geometry.pinned;
    else if (left + def.width - el.scrollLeft > el.clientWidth - geometry.right)
      el.scrollLeft = left + def.width - el.clientWidth + geometry.right;
  };

  // A cell's save, followed until the rows show it. The kept order holds it in place; when it could
  // not (the view changed while it saved), a toast says where the line is now, and Show goes there.
  const [moved, setMoved] = useState<{ line: string; col: string; from: number; orderKey: string } | null>(null);
  const showLine = useRef<(line: string, col: string) => void>(() => undefined);
  showLine.current = (lineKey, colId) => {
    const row = sorted.findIndex((r) => lineOrderKey(r.original.line) === lineKey);
    const c = Math.max(
      0,
      columns.findIndex((x) => x.id === colId),
    );
    if (row < 0) return;
    setCursor({ row, col: c });
    scrollTo({ row, col: c });
    focusGrid();
  };
  useEffect(() => {
    if (!moved) return;
    setMoved(null);
    const row = sorted.findIndex((r) => lineOrderKey(r.original.line) === moved.line);
    if (row < 0 || moved.orderKey === props.orderKey || row === moved.from) return;
    const def = columns.find((x) => x.id === moved.col);
    const now = def ? String(def.value(sorted[row]!.original) || "empty") : "";
    const id: string = toast.add({
      title: `Moved: ${now ? `it is now ${now} — ` : ""}row ${row + 1} of ${sorted.length}`,
      timeout: 8000,
      actionProps: {
        children: "Show",
        onClick: () => {
          toast.close(id);
          showLine.current(moved.line, moved.col);
        },
      },
    });
  }, [moved, sorted, props.orderKey, columns]);

  const moveBy = (dr: number, dc: number) =>
    setCursor((cur) => {
      let col = cur.col + dc;
      let row = cur.row + dr;
      if (col >= columns.length) {
        col = 0;
        row++;
      } else if (col < 0) {
        col = columns.length - 1;
        row--;
      }
      const next = { row: Math.max(0, Math.min(rowCount - 1, row)), col };
      scrollTo(next);
      return next;
    });

  // Back to the grid on the next frame — unless an editor opened since: a click's focus, late behind a
  // heavy render, took the focus from the picker one Enter had opened, and closed it (two Enters).
  const focusGrid = () =>
    requestAnimationFrame(() => {
      const now = document.activeElement;
      if (now instanceof HTMLElement && now.closest(".cell-editing, [data-cell-panel]")) return;
      scrollRef.current?.focus({ preventScroll: true });
    });

  /**
   * The big red message for a cell that cannot be typed in; then back to the grid. A Viewer's tries:
   * the View only toast, once a visit, no message (History's and No WMS's keep theirs).
   */
  const refuse = (def: ColumnDef, line?: LineView) => {
    if (!canWrite && !readOnly && !line?.noWms) return viewOnly();
    const [title, body] = line?.noWms
      ? [
          "No WMS # yet",
          "This pick ticket has no WMS # in A2000 yet, so it is on no line. Once A2000 gives it one, it joins that WMS line, where you can fill it in.",
        ]
      : readOnly
        ? [
            "History is read-only",
            "Finished lines keep the values they had. Change a line on the Worklist before it finishes.",
          ]
        : def.kind === "system"
          ? [
              `${def.header} can't be typed here`,
              `${def.header} comes from A2000. It updates here by itself when A2000 changes.`,
            ]
          : [
              `${def.header} can't be typed here`,
              `${def.header} is calculated by the tool from the routing rule and the dates.`,
            ];
    void message.error(title, body).then(focusGrid);
  };

  /** A message over an open editor: its calendar or list closes first, so nothing hides the message's buttons. */
  const [asking, setAsking] = useState(false);
  const overEditor = async <T,>(ask: () => Promise<T>) => {
    setAsking(true);
    try {
      return await ask();
    } finally {
      setAsking(false);
    }
  };

  /** A refused value: the message, then back into the open editor ("OK, I'll fix it"). */
  const refuseValue = async (title: string, body: string, where: string[]) => {
    await overEditor(() => message.error(title, body, where));
    refocusEditor(cellRef.current?.querySelector("input"));
  };

  const startEdit = (c: Cursor, initial?: string) => {
    const def = col(c.col);
    const row = at(c.row);
    if (!def || !row) return;
    // Enter on a row's first cell opens its details, but where it approves (`enterOpensDetails`).
    if (initial === undefined && props.onDetails && enterOpensDetails(c.col, def.id, row.line, locked))
      return props.onDetails(row.line);
    if (row.line.noWms) return refuse(def, row.line);
    if (def.id === "review" && !locked) {
      if (row.line.review !== "approved") void writer.approve([row.line]);
      return;
    }
    if (def.kind !== "edit" || !def.field || locked) return refuse(def);
    // A field the sources disagreed on: Enter (or a second click) opens its chooser; typing edits.
    if (initial === undefined && row.line.carry.conflicts[def.field]?.length) {
      setChoosing({ key: row.line.key, field: def.field });
      return;
    }
    // A select or list opens empty for typing; a typed letter starts the search.
    setRange(null);
    setEditing({ key: row.line.key, col: c.col, initial });
  };

  /**
   * A batch on this column (`fill.ts`): every cell checked as typed, read-only rows skipped, then
   * saved through the writer — one entry per line, one Ctrl+Z for all (`fill-run.ts`).
   */
  const runBatch = (def: ColumnDef, targets: FillTarget[], kind: "fill" | "paste", unused = 0) => {
    const plan = planFill(def, targets, { today, listValues: props.listValues, mode });
    void runFill({
      def,
      plan,
      kind,
      writer,
      message,
      onAppointment: props.onAppointment,
      rowNumber: (r) => (rowOfKey.get(r.line.key) ?? -1) + 1,
      unused,
      done: focusGrid,
    });
  };

  /**
   * Fill down (Ctrl+D, the fill handle): the range from its top cell — or, dragged, from the cell it
   * started at (`origin`, up or down) — as Excel; no range, the cell above into the cursor's.
   */
  const fill = (source: "top" | "origin") => {
    const def = col(range?.col ?? cursor.col);
    const rows = rangeLo < 0 ? null : { from: rowOfKey.get(range!.from)!, to: rowOfKey.get(range!.to)! };
    setRange(null);
    if (!def) return;
    if (def.kind !== "edit" || !def.field || locked) return refuse(def);
    const f = fillRows(rows, cursor.row, source);
    if (!f) return;
    // One cell from the one above: a read-only cell says why, as typing in it does.
    const one = f.targets.length === 1 ? at(f.targets[0]!) : undefined;
    if (one && !rows && !canEditCell(def, one, mode)) return refuse(def, one.line);
    const value = cellValue(def, at(f.source)!);
    runBatch(
      def,
      f.targets.map((i) => ({ row: at(i)!, raw: value })),
      "fill",
    );
  };

  /** The row under the pointer while the fill handle is dragged (a pick-ticket panel: its line). */
  const rowAtPointer = (clientY: number) => {
    const el = scrollRef.current;
    const shown = virtualizer.getVirtualItems();
    if (!el || !shown.length) return undefined;
    const y = clientY - el.getBoundingClientRect().top + el.scrollTop - HEADER_HEIGHT;
    const item = shown.find((v) => y >= v.start && y < v.end) ?? (y < shown[0]!.start ? shown[0] : shown.at(-1));
    return item ? items[item.index]?.index : undefined;
  };

  /** Checks and saves one cell. False keeps the editor open. */
  const commit = async (raw: string, move: Move): Promise<boolean> => {
    if (!editing) return true;
    const def = col(editing.col)!;
    const row = editRow === undefined ? undefined : at(editRow);
    if (!row || !def.field) return true;
    const line = row.line;
    const before = line.fields[def.field];
    const where = [`${line.customerName} · ${poPtText(line)}`];
    // The checks a batch makes too (`fill.ts`): a prefill left as it is stays a prefill — PE Order Ship
    // Date follows READY DATE until one is picked, a prefilled status saved as it is sets no Start or End Time.
    const checked = checkCellValue(def, row, raw, { today, listValues: props.listValues });
    if (!checked.ok) {
      await refuseValue(checked.title, checked.message, where);
      return false;
    }
    const value = checked.value;
    // A date's rule about when is an "Are you sure?" (`DATE_RULES`).
    if (
      checked.warnings.length &&
      value !== before &&
      !(await overEditor(() =>
        message.confirm("Are you sure?", `Save ${def.header} ${formatDay(value)}? Check these first:`, {
          lines: [...checked.warnings, ...where],
          confirmLabel: "Save anyway",
        }),
      ))
    ) {
      refocusEditor(cellRef.current?.querySelector("input"));
      return false;
    }
    setEditing(null);
    if (def.field === "appointmentDate" && props.onAppointment) {
      // Not a field of the line: the pickup's date. The selection's lines when this row is one of them.
      const selected = props.allLines.filter((l) => props.selection[l.key]);
      const lines = selected.length > 1 && props.selection[line.key] ? selected : [line];
      // The editor is closed already; the dialog it opens has the focus, and gives it back to the
      // grid when it closes — cancelled or done — so Esc and the arrows work again at once.
      setAsking(false);
      props.onAppointment(lines, value, focusGrid);
      return true;
    }
    if (value !== before) {
      const at = { line: lineOrderKey(line), col: def.id, from: editRow!, orderKey: props.orderKey };
      void writer.saveField(line, def.field, value).then((ok) => ok && setMoved(at));
    }
    if (move === "down") moveBy(1, 0);
    else if (move === "right") moveBy(0, 1);
    else if (move === "left") moveBy(0, -1);
    focusGrid();
    return true;
  };

  /**
   * Pasting one value into a cell opens its editor with the text: Enter saves it through the same
   * checks. Several lines (a column copied from Excel) fill down from the cell, one value per row,
   * read-only rows skipped; a value refused stops the paste at its row.
   */
  const onPaste = (e: ClipboardEvent<HTMLDivElement>) => {
    if (editing) return;
    e.preventDefault();
    const values = pastedValues(e.clipboardData.getData("text/plain"));
    if (values.length < 2) {
      if (values[0]) startEdit(cursor, values[0]);
      return;
    }
    const def = col(cursor.col);
    if (!def) return;
    if (def.kind !== "edit" || !def.field || locked) return refuse(def);
    setRange(null);
    const targets: FillTarget[] = [];
    let next = 0;
    for (let i = cursor.row; i < rowCount && next < values.length; i++) {
      const row = at(i)!;
      // A read-only row takes no value: it is skipped, the next row gets it.
      targets.push({ row, raw: canEditCell(def, row, mode) ? values[next++]! : "" });
    }
    runBatch(def, targets, "paste", values.length - next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (editing) return;
    const def = col(cursor.col);
    const row = at(cursor.row);
    if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "z") {
      e.preventDefault();
      if (locked) refuse(col(cursor.col)!);
      else void writer.undo(props.allLines);
      return;
    }
    // Fill down (`rangeKey`): Shift+↑ ↓ grows the range down the cursor's column, Ctrl+D fills it, Esc lets it go.
    const r = rangeKey(e);
    if (r?.kind === "fill") {
      e.preventDefault();
      return fill("top");
    }
    if (r?.kind === "clear") {
      if (range) setRange(null);
      return;
    }
    if (r?.kind === "extend" && row && def?.kind === "edit" && !locked) {
      e.preventDefault();
      const from = range?.col === cursor.col ? range.from : row.line.key;
      const end = range?.col === cursor.col ? (rowOfKey.get(range.to) ?? cursor.row) : cursor.row;
      const to = Math.max(0, Math.min(rowCount - 1, end + r.rows));
      setRange({ col: cursor.col, from, to: at(to)!.line.key });
      scrollTo({ row: to, col: cursor.col });
      return;
    }
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    // The Worklist's own keys; the rest are every grid's (`cellKey`).
    const keys: Record<string, () => void> = {
      " ": () => row && toggleExpanded(row.line.key),
    };
    const clear = () => {
      if (!def || !row) return;
      if (row.line.noWms) return refuse(def, row.line);
      if (def.kind !== "edit" || !def.field || locked || def.editor === "select") return refuse(def);
      // PE Order Status: emptied on purpose (its prefill too); PE Order Ship Date: back to its calculation.
      if (def.field === "peStatus") {
        if (row.line.fields.peStatus !== PE_CLEARED && def.value(row))
          void writer.saveField(row.line, "peStatus", PE_CLEARED);
      } else if (row.line.fields[def.field]) void writer.saveField(row.line, def.field, "");
    };
    const run = keys[e.key];
    if (run) {
      e.preventDefault();
      return run();
    }
    const k = cellKey(e);
    if (!k || (k.kind === "edit" && k.typed !== undefined && !def)) return;
    e.preventDefault();
    // Any other key on the grid lets the range go, as in Excel.
    setRange(null);
    if (k.kind === "clear") clear();
    else if (k.kind === "move") moveBy(k.rows, k.cols);
    else if (k.kind === "edge")
      setCursor((cur) => {
        const next = cellAfter(cur, k, rowCount, columns.length);
        scrollTo(next);
        return next;
      });
    else if (k.typed === undefined) startEdit(cursor);
    else startEdit(cursor, def?.editor === "select" ? undefined : k.typed);
  };

  const allSelected = rowCount > 0 && table.getIsAllRowsSelected();

  const pinnedStyle = (left: number): CSSProperties => ({ position: "sticky", left, zIndex: 2 });

  return (
    <div
      ref={(el) => {
        scrollRef.current = el;
        if (props.gridRef) props.gridRef.current = el;
      }}
      tabIndex={0}
      role="grid"
      aria-rowcount={rowCount}
      aria-colcount={columns.length}
      onKeyDown={onKeyDown}
      onPaste={onPaste}
      className="routing-grid relative overflow-auto rounded-md border outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
      style={{ height: props.height }}
    >
      <div style={{ width: geometry.total, minWidth: "100%" }}>
        {/* Header */}
        <div role="row" className="bg-background sticky top-0 z-20 flex border-b" style={{ height: HEADER_HEIGHT }}>
          <div
            className="cell-head bg-background flex items-center justify-center"
            style={{ ...pinnedStyle(0), width: SELECT_WIDTH, zIndex: 3 }}
          >
            {!readOnly ? (
              <Checkbox
                aria-label="Select every line"
                checked={allSelected}
                onCheckedChange={(v) => table.toggleAllRowsSelected(Boolean(v))}
              />
            ) : null}
          </div>
          <div
            className="cell-head bg-background"
            style={{ ...pinnedStyle(SELECT_WIDTH), width: EXPAND_WIDTH, zIndex: 3 }}
          />
          {columns.map((c, i) => {
            const sort = table.getColumn(c.id)?.getIsSorted();
            return (
              <div
                key={c.id}
                role="columnheader"
                className={cn(
                  "cell-head relative before:absolute before:inset-x-0 before:top-0 before:h-[3px]",
                  headerBar[c.kind],
                  c.kind === "system" ? "bg-locked" : c.kind === "calc" ? "bg-calc" : "bg-background",
                  c.sticky && i === columns.findLastIndex((x) => x.sticky) && "pinned-edge",
                )}
                style={{ width: c.width, ...(c.sticky ? { ...pinnedStyle(geometry.lefts[i]!), zIndex: 3 } : {}) }}
              >
                <HeaderMenu
                  column={c}
                  title={`${headerHint[c.kind]}${c.source ? ` · ${c.source}` : ""}${c.managerOnly ? " · Internal — managers only" : ""}`}
                  sorted={sort}
                  onSort={(dir) => props.onSortingChange(dir ? [{ id: c.id, desc: dir === "desc" }] : [])}
                  values={() => headerValues(props.headerRows?.(c.id) ?? rows, c)}
                  kept={props.columnFilters[c.id] ?? []}
                  onKeep={(vals) => props.onColumnFilter(c.id, vals)}
                  onManage={manager && c.list ? () => props.onManageList(c) : undefined}
                >
                  <span className={c.wrapHeader ? "line-clamp-2 leading-tight" : "truncate"}>{c.header}</span>
                  {sort === "asc" ? (
                    <ArrowUpIcon className="size-3 shrink-0" />
                  ) : sort === "desc" ? (
                    <ArrowDownIcon className="size-3 shrink-0" />
                  ) : null}
                  {props.columnFilters[c.id]?.length ? (
                    <span className="bg-primary size-1.5 shrink-0 rounded-full" />
                  ) : null}
                </HeaderMenu>
              </div>
            );
          })}
          <div
            className="cell-head pinned-edge-right bg-background"
            style={{ ...shipPin, width: rightWidth(mode, canWrite), zIndex: 3 }}
          >
            {/* Each part named over its buttons, as Ship is: "Details" fits its 36 px at 10 px. */}
            <span className="flex justify-center text-[10px] leading-none" style={{ width: DETAILS_WIDTH }}>
              Details
            </span>
            {ships ? (
              <span className="flex justify-center" style={{ width: SHIP_WIDTH }}>
                Ship
              </span>
            ) : null}
          </div>
        </div>

        {/* Body */}
        <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
          {rowCount === 0 ? (
            <p className="text-muted-foreground sticky left-0 p-6 text-sm">
              {mode === "worklist" ? "No open lines match these filters." : "No finished lines match these filters."}
            </p>
          ) : null}
          {virtualizer.getVirtualItems().map((v) => {
            const item = items[v.index]!;
            const tr = sorted[item.index]!;
            const row = tr.original;
            const line = row.line;
            if (item.kind === "pts") {
              return (
                <div
                  key={`${line.key}:pts`}
                  data-index={v.index}
                  ref={virtualizer.measureElement}
                  className="absolute left-0 w-full"
                  style={{ transform: `translateY(${v.start}px)` }}
                >
                  {line.excel ? (
                    <ExcelDetails line={line} />
                  ) : (
                    <PickTickets
                      // The Division filter changed: the chips start again from it.
                      key={(props.columnFilters.division ?? []).join("|")}
                      line={line}
                      keptDivisions={props.columnFilters.division ?? []}
                    />
                  )}
                </div>
              );
            }
            const selected = tr.getIsSelected();
            const tone = props.rowColors ? rowTone(line) : "";
            const saving = writer.lineState(line);
            return (
              <div
                key={line.key}
                role="row"
                aria-selected={selected}
                aria-busy={saving === "saving" || undefined}
                className={cn(
                  "routing-row absolute left-0 flex border-b",
                  tone,
                  selected && "row-selected",
                  line.mixed && "row-mixed",
                  lineStateClass(saving),
                )}
                style={{ transform: `translateY(${v.start}px)`, height: ROW_HEIGHT, width: geometry.total }}
              >
                <div
                  className={cn(
                    "cell flex items-center justify-center bg-inherit",
                    line.review === "updated" && "edge-updated",
                    (line.review === "new" || line.review === "split") && "edge-new",
                    line.mixed && "edge-mixed",
                  )}
                  style={{ ...pinnedStyle(0), width: SELECT_WIDTH }}
                >
                  {saving === "saving" ? (
                    <SavingMark />
                  ) : !readOnly && !line.noWms ? (
                    <Checkbox
                      aria-label="Select line"
                      checked={selected}
                      onCheckedChange={(v) => tr.toggleSelected(Boolean(v))}
                    />
                  ) : null}
                </div>
                <div
                  className="cell flex items-center justify-center bg-inherit"
                  style={{ ...pinnedStyle(SELECT_WIDTH), width: EXPAND_WIDTH }}
                >
                  <button
                    type="button"
                    aria-label={`${expanded[line.key] ? "Hide" : "Show"} ${line.excel ? "workbook details" : "pick tickets"}`}
                    aria-expanded={Boolean(expanded[line.key])}
                    className={cn("hover:bg-muted rounded p-0.5", phone && "p-1.5")}
                    onClick={() => toggleExpanded(line.key)}
                  >
                    <ChevronRightIcon
                      className={cn("size-4 transition-transform", expanded[line.key] && "rotate-90")}
                    />
                  </button>
                </div>
                {columns.map((c, ci) => {
                  const isCursor = cursor.row === item.index && cursor.col === ci;
                  const isEditing = editing?.key === line.key && editing.col === ci;
                  const inRange = range?.col === ci && item.index >= rangeLo && item.index <= rangeHi;
                  return (
                    <div
                      key={c.id}
                      role="gridcell"
                      ref={isCursor ? cellRef : undefined}
                      aria-readonly={c.kind !== "edit" || locked || line.noWms}
                      // A Viewer: no text cursor on a cell that looks typeable, "View only" on hover.
                      title={!canWrite && c.kind === "edit" ? "View only" : undefined}
                      className={cn(
                        "cell relative flex items-center",
                        !canWrite && "cursor-default",
                        tone && c.kind === "edit" ? "bg-inherit" : kindClass[c.kind],
                        c.align === "right" && "justify-end text-right",
                        c.sticky && ci === columns.findLastIndex((x) => x.sticky) && "pinned-edge",
                        c.field === "readyDate" && readyAfterStart(line.fields.readyDate, line.start) && "cell-warn",
                        c.field && line.outside.some((o) => o.field === c.field) && "cell-warn",
                        changeOf(line, c) && "cell-changed",
                        isCursor && "cell-cursor",
                        inRange && "cell-range",
                        inRange && item.index === rangeLo && "cell-range-top",
                        inRange && item.index === rangeHi && "cell-range-bottom",
                        isEditing && "cell-editing",
                        tone === "row-cancel" && c.kind === "system" && "line-through decoration-red-700/60",
                      )}
                      style={{ width: c.width, ...(c.sticky ? pinnedStyle(geometry.lefts[ci]!) : {}) }}
                      onMouseDown={(e) => {
                        if (isEditing) return;
                        // Shift+click down (up) the cursor's editable column: the range to fill, no editor.
                        if (e.shiftKey && ci === cursor.col && c.kind === "edit" && !locked) {
                          e.preventDefault();
                          const from = range?.col === ci ? range.from : at(cursor.row)?.line.key;
                          if (from) setRange({ col: ci, from, to: line.key });
                          focusGrid();
                          return;
                        }
                        setRange(null);
                        const again = isCursor;
                        setCursor({ row: item.index, col: ci });
                        if (again && !(e.target as HTMLElement).closest("button")) {
                          e.preventDefault();
                          startEdit({ row: item.index, col: ci });
                        } else focusGrid();
                      }}
                      onDoubleClick={() => !isEditing && startEdit({ row: item.index, col: ci })}
                    >
                      {isEditing ? (
                        <CellEditor
                          column={c}
                          line={line}
                          shown={c.prefill?.(row).value}
                          editing={editing}
                          anchor={cellRef}
                          listValues={props.listValues.filter((v) => v.list === c.list)}
                          usage={props.usage(c)}
                          manager={manager}
                          quiet={asking}
                          commit={(v, m) => void commit(v, m)}
                          cancel={() => {
                            setEditing(null);
                            focusGrid();
                          }}
                          addValue={(typed, m, alongWith = []) => {
                            const problem = multiValueProblem(c.field, typed);
                            if (problem) return void message.error(`Can't add to ${c.header}`, problem);
                            props.onAddValue(c, cleanListValue(typed), (added) => {
                              if (added === null) return focusGrid();
                              setEditing(null);
                              // Work Needed: the new value beside the ones ticked.
                              const value = isMulti(c.field) ? joinValues([...alongWith, added]) : added;
                              if (value !== line.fields[c.field!]) void writer.saveField(line, c.field!, value);
                              if (m === "down") moveBy(1, 0);
                              else if (m === "right") moveBy(0, 1);
                              else if (m === "left") moveBy(0, -1);
                              focusGrid();
                            });
                          }}
                          onManage={() => {
                            setEditing(null);
                            props.onManageList(c);
                          }}
                        />
                      ) : (
                        <Cell
                          column={c}
                          row={row}
                          mode={mode}
                          canWrite={canWrite}
                          approving={writer.approvingOf(line)}
                          onApprove={() => void writer.approve([line])}
                          choosing={choosing?.key === line.key ? choosing.field : null}
                          onChoosing={(field) => {
                            setCursor({ row: item.index, col: ci });
                            setChoosing(field ? { key: line.key, field } : null);
                            if (!field) focusGrid();
                          }}
                          onChoose={(field, value) => {
                            setChoosing(null);
                            void writer.choose(line, field, value);
                            focusGrid();
                          }}
                          onTypeNew={() => {
                            setChoosing(null);
                            setCursor({ row: item.index, col: ci });
                            setEditing({ key: line.key, col: ci, initial: "" });
                          }}
                        />
                      )}
                      {isCursor && !isEditing && !locked && canEditCell(c, row, mode) ? (
                        <span
                          className="fill-handle"
                          aria-hidden
                          title="Drag down or up to fill with this value (Ctrl+D)"
                          data-testid="fill-handle"
                          onMouseDown={(e) => e.stopPropagation()}
                          onPointerDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.setPointerCapture(e.pointerId);
                            dragging.current = { col: ci, from: line.key };
                            setRange({ col: ci, from: line.key, to: line.key });
                          }}
                          onPointerMove={(e) => {
                            const drag = dragging.current;
                            const el = scrollRef.current;
                            if (!drag || !el) return;
                            const over = rowAtPointer(e.clientY);
                            if (over === undefined) return;
                            const to = at(over)?.line.key;
                            if (to) setRange((r) => (r && r.to === to ? r : { col: drag.col, from: drag.from, to }));
                            // At the table's edge: the next row scrolls into view.
                            const box = el.getBoundingClientRect();
                            const edge =
                              e.clientY > box.bottom - 24 ? 1 : e.clientY < box.top + HEADER_HEIGHT + 24 ? -1 : 0;
                            const next = itemOfRow.get(Math.max(0, Math.min(rowCount - 1, over + edge)));
                            if (edge && next !== undefined) virtualizer.scrollToIndex(next, { align: "auto" });
                          }}
                          onPointerUp={(e) => {
                            if (!dragging.current) return;
                            e.currentTarget.releasePointerCapture(e.pointerId);
                            dragging.current = null;
                            if (range && range.from !== range.to) fill("origin");
                            else setRange(null);
                          }}
                          onPointerCancel={() => {
                            dragging.current = null;
                            setRange(null);
                          }}
                        />
                      ) : null}
                    </div>
                  );
                })}
                <div
                  className="cell pinned-edge-right flex items-center bg-background"
                  style={{ ...shipPin, width: rightWidth(mode, canWrite), zIndex: 2 }}
                >
                  <span className="flex justify-center" style={{ width: DETAILS_WIDTH }}>
                    {props.onDetails ? (
                      <Tooltip>
                        <TooltipTrigger
                          render={
                            <Button
                              size="icon-xs"
                              variant="ghost"
                              aria-label="Details"
                              data-testid="row-details"
                              onClick={() => props.onDetails?.(line)}
                            />
                          }
                        >
                          <PanelRightOpenIcon className="size-3.5" />
                        </TooltipTrigger>
                        <TooltipContent>Fields, pick tickets, shipment and history</TooltipContent>
                      </Tooltip>
                    ) : null}
                  </span>
                  {/* Not rendered for a Viewer (`ships`), as the header's Ship and the width: never clipped. */}
                  {ships ? (
                    <span className="flex items-center justify-center" style={{ width: SHIP_WIDTH }}>
                      {line.noWms ? null : (
                        <Tooltip>
                          <TooltipTrigger
                            render={
                              <Button
                                size="xs"
                                variant="outline"
                                className="gap-1"
                                aria-label="Mark shipped"
                                data-testid="row-mark-shipped"
                                onClick={() => props.onShip?.(line)}
                              />
                            }
                          >
                            <TruckIcon className="size-3.5" />
                            Ship
                          </TooltipTrigger>
                          <TooltipContent>
                            <p className="font-semibold">Mark shipped</p>
                            {line.onPickup
                              ? `On the pickup ${line.onPickup.pickup.pickupDate ? formatDay(line.onPickup.pickup.pickupDate) : ""} · ${line.onPickup.pickup.carrier} · ${line.onPickup.pickup.loadRef}`
                              : "Not on the Shipping schedule: ship it on its own (parcel, customer pickup…)"}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </span>
                  ) : null}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function CellEditor({
  column,
  line,
  editing,
  manager,
  onManage,
  shown,
  ...rest
}: Omit<EditorProps, "value" | "initial"> & {
  line: LineView;
  /** What a prefilled cell shows (`ColumnDef.prefill`): its editor opens on it. */
  shown?: string;
  editing: Editing;
  manager: boolean;
  onManage: () => void;
}) {
  // The Appointment date opens on the pickup's date (item 8), else the date stored before.
  const value =
    column.field === "appointmentDate"
      ? appointmentCell(line).value || appointmentCell(line).notScheduled
      : column.editor === "estimate"
        ? estimateText(line.fields.peEstimate)
        : (shown ?? line.fields[column.field!]);
  const props: EditorProps = { ...rest, column, value, initial: editing.initial };
  switch (column.editor) {
    case "date":
      return <DateEditor {...props} />;
    case "select":
      return <SelectEditor {...props} />;
    case "list":
      return isMulti(props.column.field) ? (
        <MultiListEditor {...props} manager={manager} onManage={onManage} />
      ) : (
        <ListEditor {...props} manager={manager} onManage={onManage} />
      );
    case "suggest":
      return <SuggestEditor {...props} />;
    case "moment":
      return <MomentEditor {...props} />;
    default:
      return <TextEditor {...props} />;
  }
}

const shortDay = (d: string | number | null | undefined) =>
  typeof d === "string" && isDay(d) ? formatDay(d) : typeof d === "number" ? d.toLocaleString("en-US") : (d ?? "—");

type CellChange = { from: string | number | null | undefined; to: string | number | null | undefined };
/** The line's earliest date is as approved, but a pick ticket's own one moved: marked, the value kept. */
const PT_ONLY: CellChange = { from: undefined, to: undefined };
/** What can't be read: an Updated line with no field to name (its baseline is unreadable). */
const UNREADABLE: CellChange = { from: null, to: null };

/**
 * Where a line shows what changed since approval: each changed field on its own column
 * (`ColumnDef.change`), a date some pick ticket changed though the earliest did not too
 * (`ptChanges`); an Updated line with no field to name on SO / PT #. Approving clears them.
 */
const changeOf = (line: LineView, c: ColumnDef): CellChange | undefined =>
  c.change && line.changes[c.change]
    ? line.changes[c.change]
    : c.change && line.ptChanges.some((x) => x.field === c.change)
      ? PT_ONLY
      : c.id === "pt" && line.review === "updated" && !Object.keys(line.changes).length && !line.ptChanges.length
        ? UNREADABLE
        : undefined;

/** "— changed by Isaac Shalita on 10/28, 04:12 PM": the latest change of the field, read on hover only. */
function ChangedBy({ line, field }: { line: LineView; field: string }) {
  const id = line.stored && !line.stored.id.startsWith("pending:") ? line.stored.id : "";
  const changes = useChanges({ line: id }, !!id);
  const last = changes.data?.changes.find((c) => c.entity === ROUTING_LINES.id && c.changes.some((x) => x.f === field));
  if (last)
    return (
      <>
        {" "}
        — changed by {last.personName} on {changedAt(last)}
      </>
    );
  return changes.isPending && id ? <> — …</> : null;
}

/**
 * The marker of a change: the triangle, and on hover what it was and is. With `children` (the cell's
 * old → new or value) it is the whole cell: hovering anywhere on it says so.
 */
function ChangeMark({
  line,
  change,
  field,
  align,
  children,
}: {
  line: LineView;
  change: CellChange;
  field?: ChangedField;
  align?: ColumnDef["align"];
  children?: ReactNode;
}) {
  const on = line.stored?.reviewedAt ? ` on ${formatDay(line.stored.reviewedAt.slice(0, 10))}` : "";
  const was = (v: unknown) => shortDay(v as string | number | null);
  const pts = line.ptChanges.filter((x) => x.field === field);
  const team = !!field && isTeamReviewField(field);
  const icon = (
    <TriangleAlertIcon
      className="size-3.5 shrink-0 text-amber-500"
      // Who changed a team date is read on hover only (`ChangedBy`).
      aria-label={team ? "Changed in the app" : `Changed by ${line.source}`}
      data-testid="change-mark"
    />
  );
  return (
    <Tooltip>
      {children === undefined ? (
        <TooltipTrigger render={icon} />
      ) : (
        <TooltipTrigger
          render={
            <span
              className={cn(
                "flex min-w-0 flex-1 items-center gap-1 self-stretch px-2",
                align === "right" && "justify-end",
              )}
              data-testid="change-cell"
            />
          }
        >
          {icon}
          {children}
        </TooltipTrigger>
      )}
      <TooltipContent className="max-w-md">
        {change === UNREADABLE ? (
          `Updated in ${line.source} — what was approved can't be read; approve the line again`
        ) : team ? (
          <>
            Was {was(change.from)}, now {was(change.to)}
            <ChangedBy line={line} field={field} />
          </>
        ) : change === PT_ONLY ? (
          `Changed by ${line.source} since it was approved${on}`
        ) : (
          `Was ${was(change.from)}, now ${was(change.to)} — changed by ${line.source} since it was approved${on}`
        )}
        {" · approve the line to confirm"}
        {pts.length ? (
          <ul className="mt-1">
            {pts.map((x) => (
              <li key={x.pickTicket}>{ptChangeText(x)}</li>
            ))}
          </ul>
        ) : null}
      </TooltipContent>
    </Tooltip>
  );
}

/** A cell as it reads: the chips, old → new, every value of a Mixed line, the "2 values" marker. */
function Cell({
  column: c,
  row,
  mode,
  canWrite,
  approving,
  onApprove,
  choosing,
  onChoosing,
  onChoose,
  onTypeNew,
}: {
  column: ColumnDef;
  row: Row;
  mode: Mode;
  canWrite: boolean;
  approving: { done: number; total: number } | undefined;
  onApprove: () => void;
  /** The field of this line whose "2 values" chooser is open, if any. */
  choosing: WarehouseField | null;
  onChoosing: (field: WarehouseField | null) => void;
  /** A value chosen for a field the lines it came from disagreed on. */
  onChoose: (field: WarehouseField, value: string) => void;
  onTypeNew: () => void;
}) {
  const line = row.line;
  const conflict = c.field && mode === "worklist" ? line.carry.conflicts[c.field] : undefined;
  if (conflict?.length)
    return (
      <ConflictCell
        column={c}
        row={row}
        open={choosing === c.field}
        onOpenChange={(o) => onChoosing(o ? c.field! : null)}
        onChoose={canWrite ? onChoose : undefined}
        onTypeNew={onTypeNew}
      />
    );
  if (c.id === "finished") return <FinishedChip finished={line.finished} />;
  if (c.id === "review")
    return (
      <ReviewChip
        review={line.review}
        // No buttons on History, nor for a Viewer.
        active={mode === "worklist" && !line.noWms && canWrite}
        approving={approving}
        onApprove={onApprove}
      />
    );
  if (c.id === "window") {
    if (mode === "history" || line.allGone)
      return (
        <span className="text-muted-foreground truncate px-2">
          {line.allGone && !line.finished ? `Gone from ${line.source}` : "—"}
        </span>
      );
    return <WindowChip row={row} />;
  }
  const change = changeOf(line, c);
  const body: ReactNode = c.value(row);
  // A date or QTY: old → new in the cell (a date only some pick ticket's changed: its value), the
  // whole cell the marker's hover; PO and SO / PT # keep their text and its hover, the marker beside it.
  if (change === PT_ONLY)
    return (
      <ChangeMark line={line} change={change} field={c.change} align={c.align}>
        <span className="truncate">{body}</span>
      </ChangeMark>
    );
  if (change && c.change !== "po" && c.change !== "pts")
    return (
      <ChangeMark line={line} change={change} field={c.change} align={c.align}>
        <span className="text-muted-foreground line-through">{shortDay(change.from)}</span>
        <span>→</span>
        <span className="font-semibold">{shortDay(change.to)}</span>
      </ChangeMark>
    );
  if (c.id === "customer") {
    return (
      <span className="flex min-w-0 items-center gap-1.5 px-2 font-medium">
        {line.outside.length ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <TriangleAlertIcon className="size-3.5 shrink-0 text-orange-600" aria-label="Changed outside the app" />
              }
            />
            <TooltipContent>Changed outside the app: {outsideText(line.outside)}</TooltipContent>
          </Tooltip>
        ) : null}
        {line.excel?.oddDate ? (
          <Tooltip>
            <TooltipTrigger
              render={<TriangleAlertIcon className="size-3.5 shrink-0 text-amber-500" aria-label="Odd date" />}
            />
            <TooltipContent>A date in the old workbook looked wrong; original text kept</TooltipContent>
          </Tooltip>
        ) : null}
        <NamedText items={customerItems(line)} />
      </span>
    );
  }
  if (c.id === "division") return <DivisionText divs={line.divs} className="min-w-0 truncate px-2" />;
  if ((c.id === "start" || c.id === "cancel" || c.id === "ptDate") && !change) {
    const all = (c.id === "start" ? line.starts : c.id === "cancel" ? line.cancels : line.ptDates).filter(Boolean);
    if (all.length > 1) return <MoreText short={String(c.value(row))} full={all.map((d) => formatDay(d)).join(", ")} />;
  }
  if (c.id === "wms") {
    return (
      <span className="flex min-w-0 items-center gap-1.5 px-2">
        <span className="truncate">{c.value(row)}</span>
        {line.mixed ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <span className="shrink-0 rounded bg-violet-600 px-1.5 py-0.5 text-[11px] font-bold text-white" />
              }
            >
              Mixed
            </TooltipTrigger>
            <TooltipContent>{mixedText(line.mixed)} in this WMS — allowed, information only</TooltipContent>
          </Tooltip>
        ) : null}
      </span>
    );
  }
  if (c.id === "a2000") return line.a2000 ? <A2000Chip shipped={line.a2000} /> : null;
  if (c.id === "source")
    return (
      <span className="px-2">
        <SourceBadge source={line.source} />
      </span>
    );
  if (c.id === "deposcoStatus") return line.deposco?.status ? <DeposcoStatusChip status={line.deposco.status} /> : null;
  if (c.id === "dshipped") return line.deposco?.shipped ? <DeposcoChip shipped={line.deposco.shipped} /> : null;
  if (c.id === "po")
    return change ? (
      <span className="flex min-w-0 items-center gap-1.5 px-2">
        <ChangeMark line={line} change={change} />
        <MoreText
          short={poText(line, PO_PT_SHOWN)}
          full={poText(line)}
          prefix={countPrefix(poCount(line))}
          className="min-w-0 truncate"
        />
      </span>
    ) : (
      <MoreText short={poText(line, PO_PT_SHOWN)} full={poText(line)} prefix={countPrefix(poCount(line))} />
    );
  if (c.id === "pt") {
    return (
      <span className="flex min-w-0 items-center gap-1.5 px-2">
        {change ? <ChangeMark line={line} change={change} /> : null}
        {line.carry.from ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <CornerDownRightIcon className="size-3.5 shrink-0 text-violet-600" aria-label="Came from other lines" />
              }
            />
            <TooltipContent className="max-w-md">
              Came from {carriedFromLabel(line.carry.from)}
              {line.carry.filled.length ? " — with their entries" : ""}
            </TooltipContent>
          </Tooltip>
        ) : null}
        <PreRoutedMarker line={line} />
        <PtText line={line} />
      </span>
    );
  }
  if (c.id === "finishedAt" && line.excel?.approxFrom) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="flex min-w-0 items-center gap-1 px-2" />}>
          <span className="truncate">{body}</span>
          <span className="text-muted-foreground text-[11px]">approx.</span>
        </TooltipTrigger>
        <TooltipContent>
          Approximate: the old workbook had no scheduled ship date, so this is its {line.excel.approxFrom}
        </TooltipContent>
      </Tooltip>
    );
  }
  const outside = c.field ? line.outside.find((o) => o.field === c.field) : undefined;
  if (outside) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="truncate px-2" />}>{body}</TooltipTrigger>
        <TooltipContent>
          Changed outside the app: {outside.label} — {outside.why}. Edit the cell to fix it.
        </TooltipContent>
      </Tooltip>
    );
  }
  if (c.id === "readyDate" && readyAfterStart(line.fields.readyDate, line.start)) {
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="truncate px-2" />}>{body}</TooltipTrigger>
        <TooltipContent>
          READY DATE is after the first ship date ({line.start ? formatDay(line.start) : ""})
        </TooltipContent>
      </Tooltip>
    );
  }
  // Work Needed's several values: comma-separated, each on its own line on hover.
  // A date stored before on a line on no pickup (item 8): muted, and how to schedule it.
  if (c.id === "appointmentDate" && appointmentCell(line).notScheduled)
    return (
      <Tooltip>
        <TooltipTrigger
          render={
            <span className="text-muted-foreground min-w-0 truncate px-2 italic" data-testid="appt-not-scheduled" />
          }
        >
          {formatDay(appointmentCell(line).notScheduled)}
        </TooltipTrigger>
        <TooltipContent>Not on a pickup yet — enter the date again to schedule</TooltipContent>
      </Tooltip>
    );
  // A value the app works out (PE Order Ship Date, PE Order Status): muted until someone edits the cell.
  const pre = c.prefill?.(row);
  if (pre?.prefill)
    return (
      <Tooltip>
        <TooltipTrigger
          render={<span className="text-muted-foreground min-w-0 truncate px-2 italic" data-testid="cell-prefill" />}
        >
          {body}
        </TooltipTrigger>
        <TooltipContent>{pre.from} — not saved until someone edits the cell</TooltipContent>
      </Tooltip>
    );
  const several = isMulti(c.field) ? splitValues(line.fields[c.field!]) : [];
  if (several.length > 1)
    return (
      <Tooltip>
        <TooltipTrigger render={<span className="min-w-0 truncate px-2" />}>{body}</TooltipTrigger>
        <TooltipContent className="max-h-72 max-w-md overflow-y-auto">
          <ul className="space-y-0.5">
            {several.map((v) => (
              <li key={v}>{v}</li>
            ))}
          </ul>
        </TooltipContent>
      </Tooltip>
    );
  return <span className="min-w-0 truncate px-2">{body}</span>;
}

/** "Shipped in A2000 · 10/19" or "2 of 5 PTs shipped in A2000"; each pick ticket on hover. A flag only. */
export function A2000Chip({ shipped }: { shipped: A2000Shipped }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              "mx-2 inline-flex h-6 min-w-0 items-center truncate rounded px-2 text-[11px] font-semibold whitespace-nowrap ring-1 ring-inset",
              shipped.all ? "bg-teal-600 text-white ring-teal-700" : "bg-teal-50 text-teal-800 ring-teal-300",
            )}
            data-testid="a2000-chip"
          />
        }
      >
        {a2000Text(shipped)}
      </TooltipTrigger>
      {/* One column (the platform's tooltip lays its children in a row), the caption first; a long
          list scrolls instead of running off the screen. Pick tickets alike are one line (a2000Lines). */}
      <TooltipContent className="max-w-md">
        <div className="flex max-h-[min(20rem,60vh)] flex-col gap-1 overflow-y-auto" data-testid="a2000-tooltip">
          <p className="font-medium">
            A2000 shows these pick tickets shipped — mark the line shipped here when it left
          </p>
          <ul className="space-y-0.5">
            {a2000Lines(shipped).map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A Deposco line's status: its SOs' highest, "MIXED" in violet when they differ; each SO and its
 * status on hover, the one behind first, so the team can fix it in Deposco.
 */
function DeposcoStatusChip({ status }: { status: DeposcoStatus }) {
  return (
    <Tooltip>
      <TooltipTrigger render={<span className="flex min-w-0 items-center gap-1.5 px-2" />}>
        <span className="truncate">{status.status}</span>
        {status.mixed ? (
          <span className="shrink-0 rounded bg-violet-600 px-1.5 py-0.5 text-[11px] font-bold text-white">MIXED</span>
        ) : null}
        {status.canceled && status.status !== "Canceled" ? (
          <span className="text-muted-foreground shrink-0 text-[11px]">{status.canceled} canceled</span>
        ) : null}
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <div
          className="flex max-h-[min(20rem,60vh)] flex-col gap-1 overflow-y-auto"
          data-testid="deposco-status-tooltip"
        >
          <p className="font-medium">
            {deposcoStatusText(status)}
            {status.mixed ? " — the SOs differ: fix them in Deposco" : ""}
          </p>
          <ul className="space-y-0.5">
            {status.sos.map((x) => (
              <li key={x.so}>
                {x.so} · {x.status || "—"}
              </li>
            ))}
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/** "Shipped in Deposco · 10/19" or "2 of 5 SOs shipped in Deposco"; each SO on hover. A flag only. */
function DeposcoChip({ shipped }: { shipped: DeposcoShipped }) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            className={cn(
              "mx-2 inline-flex h-6 min-w-0 items-center truncate rounded px-2 text-[11px] font-semibold whitespace-nowrap ring-1 ring-inset",
              shipped.all ? "bg-teal-600 text-white ring-teal-700" : "bg-teal-50 text-teal-800 ring-teal-300",
            )}
            data-testid="deposco-chip"
          />
        }
      >
        {deposcoShippedText(shipped)}
      </TooltipTrigger>
      <TooltipContent className="max-w-md">
        <div className="flex max-h-[min(20rem,60vh)] flex-col gap-1 overflow-y-auto">
          <p className="font-medium">Deposco shows these SOs shipped — mark the line shipped here when it left</p>
          <ul className="space-y-0.5">
            {deposcoShippedLines(shipped).map((l) => (
              <li key={l}>{l}</li>
            ))}
          </ul>
        </div>
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * A header's "Show values": each value once, as the cell says it; with both sources on the page,
 * each says which one it comes from ("ROSS STORES · A2000 + Deposco").
 */
function headerValues(rows: readonly Row[], c: ColumnDef): { value: string; label?: string }[] {
  const seen = new Map<string, { label?: string; sources: Set<LineSource> }>();
  for (const r of rows) {
    for (const { value, label } of c.values?.(r) ?? [{ value: c.value(r), label: c.label?.(r) }]) {
      const had = seen.get(value);
      if (had) had.sources.add(r.line.source);
      else seen.set(value, { label, sources: new Set([r.line.source]) });
    }
  }
  const both = new Set(rows.map((r) => r.line.source)).size > 1;
  return [...seen]
    .map(([value, v]) => ({
      value,
      label: both && value ? `${v.label ?? value} · ${[...v.sources].sort().join(" + ")}` : v.label,
    }))
    .sort((a, b) => (a.label ?? a.value).localeCompare(b.label ?? b.value));
}

/** The first few PT ranges or POs, "+N more"; all of them in the tooltip and the expanded rows. */
const PO_PT_SHOWN = 3;
function PtText({ line }: { line: LineView }) {
  // A workbook row: its PO / PT # as typed, nothing worked out.
  if (line.excel)
    return (
      <span className="truncate" title={line.excel.poPt}>
        {line.excel.poPt}
      </span>
    );
  return (
    <MoreText
      short={ptText(line, PO_PT_SHOWN)}
      full={ptText(line)}
      prefix={countPrefix(ptCount(line))}
      className="min-w-0 truncate"
    />
  );
}
/**
 * A cell's first few values; the rest on hover. Padded as a plain cell unless `className` says
 * otherwise. `prefix` ("(2) ", `countPrefix`) is shown only: the column's value has none.
 */
function MoreText({
  short,
  full,
  prefix = "",
  className = "min-w-0 truncate px-2",
}: {
  short: string;
  full: string;
  prefix?: string;
  className?: string;
}) {
  if (short === full)
    return (
      <span className={className}>
        {prefix}
        {full}
      </span>
    );
  return (
    <Tooltip>
      <TooltipTrigger render={<span className={className} />}>
        {prefix}
        {short}
      </TooltipTrigger>
      <TooltipContent className="max-h-72 max-w-md overflow-y-auto break-words">{full}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A field the lines a pick ticket came from disagreed on (`carry.ts`): the line's own value (or
 * blank) and an amber "N values" marker; choosing one, or typing a new one, settles it. The grid
 * owns the chooser (`open`): one click on the marker, or Enter on the cell, opens it. The
 * marker keeps its mouse to itself — the cell would take the focus back and close it.
 */
function ConflictCell({
  column: c,
  row,
  open,
  onOpenChange,
  onChoose,
  onTypeNew,
}: {
  column: ColumnDef;
  row: Row;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for a Viewer: the values are shown, none can be picked. */
  onChoose?: (field: WarehouseField, value: string) => void;
  onTypeNew: () => void;
}) {
  const line = row.line;
  const field = c.field!;
  const own = line.fields[field];
  const offered = line.carry.conflicts[field] ?? [];
  const choices = [
    ...(own && !(field === "routingStatus" && own === "Not routed") ? [{ value: own, from: "this line" }] : []),
    ...offered.map((o) => ({ value: o.value, from: conflictSource(o) })),
  ].filter((v, i, all) => all.findIndex((x) => x.value === v.value) === i);
  const shown = (v: string) => (c.editor === "date" && isDay(v) ? formatDay(v) : v);
  const keep = (e: { stopPropagation: () => void }) => e.stopPropagation();
  return (
    <span className="flex min-w-0 items-center gap-1.5 px-2">
      <span className="min-w-0 truncate">{shown(own)}</span>
      <Popover open={open} onOpenChange={onOpenChange}>
        <PopoverTrigger
          render={
            <button
              type="button"
              data-testid="conflict-marker"
              className="shrink-0 rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 hover:bg-amber-200"
              aria-label={`${c.header}: ${choices.length} values${onChoose ? " to choose from" : ""}`}
              onPointerDown={keep}
              onMouseDown={keep}
              onDoubleClick={keep}
            />
          }
        >
          {choices.length} values
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-96 gap-1 p-2 text-[13px]"
          data-testid="conflict-chooser"
          onMouseDown={keep}
          onKeyDown={keep}
        >
          <p className="text-muted-foreground px-1 pb-1 text-xs">
            The lines this one came from disagree on {c.header}.{onChoose ? " Choose one, or type a new one." : ""}
          </p>
          {choices.map((ch) =>
            onChoose ? (
              <button
                key={ch.value}
                type="button"
                className="hover:bg-muted focus-visible:bg-muted flex w-full flex-col items-start rounded px-2 py-1 text-left outline-none"
                onClick={() => onChoose(field, ch.value)}
              >
                <span className="w-full truncate font-medium">{shown(ch.value)}</span>
                <span className="text-muted-foreground w-full truncate text-xs">{ch.from}</span>
              </button>
            ) : (
              <span key={ch.value} className="flex w-full flex-col items-start px-2 py-1">
                <span className="w-full truncate font-medium">{shown(ch.value)}</span>
                <span className="text-muted-foreground w-full truncate text-xs">{ch.from}</span>
              </span>
            ),
          )}
          {onChoose ? (
            <button
              type="button"
              className="text-primary w-full border-t px-2 pt-1.5 text-left hover:underline"
              onClick={onTypeNew}
            >
              Type a new one…
            </button>
          ) : null}
        </PopoverContent>
      </Popover>
    </span>
  );
}

/** Where a competing value came from, as the team knows it: "from WMS 37092 · TJ MAXX VIA EDI · PT 1807695". */
const conflictSource = (o: { fromWms: string; fromLine: string; about?: string }) =>
  o.about || (o.fromWms ? `from WMS ${o.fromWms}` : "from an earlier line");

/** A workbook row opened: what the old workbook said, as typed. */
function ExcelDetails({ line }: { line: LineView }) {
  const x = line.excel!;
  const facts: [string, string][] = [
    ["From", `${x.sheet || "Shipped Orders 2026"} · row ${x.sourceRow}`],
    ["PO / PT #", x.poPt],
    ["First PT #", x.firstPickTicket],
    ["WMS", line.wms],
    ["Kind", x.kind],
    ["INV", x.inv],
    ["Finished on", x.approxFrom ? `approx. — the workbook's ${x.approxFrom}` : "the scheduled ship date"],
  ];
  return (
    <div className="bg-muted/40 border-b py-2 pr-4" style={{ paddingLeft: SELECT_WIDTH + EXPAND_WIDTH }}>
      <dl
        className="sticky grid w-fit max-w-3xl grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 px-3 text-[12.5px]"
        style={{ left: SELECT_WIDTH + EXPAND_WIDTH }}
      >
        {facts
          .filter(([, v]) => v)
          .map(([k, v]) => (
            <div key={k} className="contents">
              <dt className="text-muted-foreground">{k}</dt>
              <dd className="break-words">{v}</dd>
            </div>
          ))}
      </dl>
      <p className="text-muted-foreground px-3 pt-1 text-xs">
        {EXCEL_SOURCE_LABEL} · read-only{x.duplicate ? " · a duplicate in the workbook" : ""}
        {x.oddDate ? " · a date looked wrong; original text kept" : ""}
      </p>
    </div>
  );
}

/**
 * The pick tickets inside a line (spec Figure 15): where each came from, and those A2000 moved away.
 * Several divisions: chips above the table filter its rows and its totals; the Worklist's Division
 * filter picks the chips it opens with ("Show all N" shows the rest).
 */
/** A pick ticket's own date in its row: old → new, marked as on the line, when it changed since approval. */
function PtDay({ line, p, field, text }: { line: LineView; p: PtView; field: PtChange["field"]; text: string }) {
  const x = line.ptChanges.find((c) => c.pickTicket === p.pickTicket && c.field === field);
  if (!x) return <>{text}</>;
  return (
    <span
      className="inline-flex items-center gap-1 whitespace-nowrap"
      title={`Was ${shortDay(x.from)}, now ${shortDay(x.to)} — changed by ${line.source} since it was approved · approve the line to confirm`}
      data-testid="pt-change"
    >
      <TriangleAlertIcon className="size-3.5 shrink-0 text-amber-500" aria-label={`Changed by ${line.source}`} />
      <span className="text-muted-foreground line-through">{shortDay(x.from)}</span>
      <span>→</span>
      <span className="font-semibold">{shortDay(x.to)}</span>
    </span>
  );
}

function PickTickets({ line, keptDivisions }: { line: LineView; keptDivisions: readonly string[] }) {
  const chips = divisionChips(line.divs);
  const [chosen, setChosen] = useState(() => (chips.length > 1 ? chipsOfFilter(line.divs, keptDivisions) : []));
  const shownPts = line.pts.filter((p) => inChips(p, chosen));
  const shownRemoved = line.removed.filter((p) => inChips(p, chosen));
  const hidden = line.pts.length + line.removed.length - shownPts.length - shownRemoved.length;
  const units = shownPts.filter((p) => !p.gone).reduce((n, p) => n + p.qty, 0);
  const deposco = line.source === "Deposco";
  // The divisions by their codes ("2 divisions (H, TX)"): names alike would count as one.
  const mixed = line.mixed ? mixedText({ ...line.mixed, divisions: 0 }) : "";
  const divs = divisionsNote(line.divs);
  return (
    <div className="bg-muted/40 border-b py-2 pr-4" style={{ paddingLeft: SELECT_WIDTH + EXPAND_WIDTH }}>
      <div className="sticky w-fit" style={{ left: SELECT_WIDTH + EXPAND_WIDTH }}>
        {chips.length > 1 ? (
          <div className="flex flex-wrap items-center gap-1 px-3 pb-1" data-testid="division-chips">
            {[{ key: "", text: "All", tip: "Every division" }, ...chips].map((c) => {
              const on = c.key ? chosen.includes(c.key) : !chosen.length;
              return (
                <button
                  key={c.key || "all"}
                  type="button"
                  title={c.tip}
                  aria-pressed={on}
                  className={cn(
                    "rounded-full border px-2 py-0.5 text-[11px] font-medium",
                    on ? "bg-foreground text-background border-foreground" : "bg-background hover:bg-muted",
                  )}
                  onClick={() =>
                    setChosen((x) => (!c.key ? [] : x.includes(c.key) ? x.filter((k) => k !== c.key) : [...x, c.key]))
                  }
                >
                  {c.text}
                </button>
              );
            })}
            {hidden ? (
              <button
                type="button"
                className="text-primary ml-1 text-[11px] hover:underline"
                onClick={() => setChosen([])}
              >
                Show all {line.pts.length + line.removed.length}
              </button>
            ) : null}
          </div>
        ) : null}
        <table className="text-[12.5px]">
          <thead className="text-muted-foreground text-left">
            <tr className="[&>th]:px-3 [&>th]:py-1 [&>th]:font-medium">
              <th>{deposco ? "SO #" : "Pick ticket"}</th>
              <th>Customer</th>
              <th>Division</th>
              <th>PO</th>
              <th>Store</th>
              <th className="text-right">QTY</th>
              <th>Received</th>
              <th>First ship</th>
              <th>Cancel</th>
              {deposco ? <th>Deposco status</th> : null}
              <th>Source</th>
            </tr>
          </thead>
          <tbody>
            {[...shownPts, ...shownRemoved].map((p) => {
              const left = line.removed.includes(p);
              return (
                <tr
                  key={p.pickTicket}
                  className={cn("[&>td]:px-3 [&>td]:py-1", (p.gone || left) && "text-muted-foreground line-through")}
                >
                  <td className="font-medium">{p.pickTicket}</td>
                  <td>
                    {p.customerCode || p.customerName ? (
                      <NamedText
                        items={[customerFaceOf(line, { code: p.customerCode, name: p.customerName })]}
                        className="whitespace-nowrap"
                      />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    {p.division ? <NamedText items={[ptDivision(line.divs, p)]} className="whitespace-nowrap" /> : "—"}
                  </td>
                  <td>{p.customerPo || "—"}</td>
                  <td>{p.store || "—"}</td>
                  <td className="text-right tabular-nums">{p.qty.toLocaleString("en-US")}</td>
                  <td>
                    <PtDay line={line} p={p} field="ptDate" text={p.ptDate ? formatDay(p.ptDate) : "—"} />
                  </td>
                  <td>
                    <PtDay
                      line={line}
                      p={p}
                      field="start"
                      text={p.start ? `${dayOfWeek(p.start)} ${formatDay(p.start)}` : "—"}
                    />
                  </td>
                  <td>
                    <PtDay line={line} p={p} field="cancel" text={p.cancel ? formatDay(p.cancel) : "—"} />
                  </td>
                  {deposco ? <td>{p.deposco?.status || "—"}</td> : null}
                  <td>
                    {p.gone ? (
                      <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[11px] font-medium text-slate-700 no-underline">
                        {deposco
                          ? goneWhy(
                              p.deposco
                                ? { deposco_status: p.deposco.status, actual_ship_date: p.deposco.shippedOn }
                                : undefined,
                            )
                          : "No longer open in A2000"}
                      </span>
                    ) : left ? (
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium text-amber-800 no-underline">
                        {deposco ? "On another Deposco line now" : p.wms ? `Now in WMS ${p.wms}` : "No WMS # now"}
                      </span>
                    ) : deposco ? (
                      [
                        "Deposco",
                        p.dc ? `DC ${p.dc}` : "",
                        p.deposco?.group ? `group ${p.deposco.group}` : "",
                        p.wh,
                        p.cameFrom ? `came from ${p.cameFrom}` : "",
                      ]
                        .filter(Boolean)
                        .join(" · ")
                    ) : (
                      `A2000 · WMS ${p.wms || "—"} · ${p.wh}${p.cameFrom ? ` · came from ${p.cameFrom}` : ""}`
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <p className="text-muted-foreground px-3 pt-1 text-xs">
          {hidden ? `${shownPts.length} of ` : ""}
          {line.pts.length} {deposco ? "SO" : "pick ticket"}
          {line.pts.length === 1 ? "" : "s"} · {units.toLocaleString("en-US")} units ·{" "}
          {line.noWms
            ? "no WMS # yet in A2000"
            : deposco
              ? `one line per customer + PO${line.dcs.length ? " + DC" : ""} (Deposco)${line.deposco?.status?.mixed ? " · MIXED statuses" : ""}${divs ? ` · ${divs}` : ""}`
              : `one line per WMS # (A2000)${mixed ? ` · Mixed: ${mixed}` : ""}${divs ? ` · ${divs}` : ""}`}
        </p>
      </div>
    </div>
  );
}
